import { useEffect, useState, type RefObject } from "react";

/**
 * The content box of an element, followed as it changes — so a drawing can
 * be written in the element's own pixels (one viewBox unit = one CSS pixel,
 * the same reason `OutlineDraft` measures its board): figures keep their
 * 11 px whatever size the pane has.
 *
 * jsdom has no `ResizeObserver` and no layout; `fallback` is what a test
 * measures against, and it is a real size.
 */
export function useBoxSize(
  ref: RefObject<Element | null>,
  fallback: { width: number; height: number },
): { width: number; height: number } {
  const [size, setSize] = useState(fallback);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry?.contentRect.width ?? 0);
      const height = Math.round(entry?.contentRect.height ?? 0);
      if (width > 0 && height > 0) {
        setSize((was) => (was.width === width && was.height === height ? was : { width, height }));
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}
